import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { ELLIPSIS_CHARACTER } from '../../../typography/utils.js';
import {
  truncateString,
  type TruncationPriority,
} from '../../core/components/text-ellipsis/truncate-string.js';
import type { TextMeasurer } from '../../core/utils/text-measure.js';
import {
  AUTO_TEXT_SIZE_PRESETS,
  DEFAULT_FONT_WEIGHT,
  DEFAULT_LABEL_FONTSIZE,
  DEFAULT_LABEL_PADDING,
  INCREASED_FONT_WEIGHT,
} from '../constants.js';
import { LabelLayout } from '../types/canvas.js';
import type { TreeMapRect } from '../types/treemap.js';

export interface TreeMapLabel {
  text: string;
  fontConfig: FontConfig;
  position: { x: number; y: number };
}

interface FontConfig {
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
}

interface LabelsConfig {
  name: string;
  value: string;
  withValueOnly: boolean;
  textSize: 'auto' | number;
  truncationMode: TruncationMode;
}

/**
 * Builds the list of labels to render inside a tree-map node.
 * Delegates to either a value-only or a name+value layout based on `labelsConfig.withValueOnly`.
 * @param labelsConfig - display configuration including name, value, truncation mode, and text size.
 * @param treemapRect - bounding rectangle of the tree-map node.
 * @param textMeasurer - function used to measure rendered text width at a given font size.
 * @returns An array of `TreeMapLabel` objects ready for canvas rendering.
 */
export const buildLabels = (
  labelsConfig: LabelsConfig,
  treemapRect: TreeMapRect,
  textMeasurer: TextMeasurer,
): TreeMapLabel[] => {
  const { withValueOnly } = labelsConfig;
  const { width, height } = treemapRect;
  const textTruncator = getTextTruncator(textMeasurer);
  const availableWidth = width - DEFAULT_LABEL_PADDING * 2;
  const labelPosition = getLabelPosition(treemapRect);
  const labelsRect: TreeMapRect = {
    ...labelPosition,
    width: availableWidth,
    height,
  };

  if (withValueOnly) {
    return buildValueLabel(
      labelsConfig,
      labelsRect,
      textMeasurer,
      textTruncator,
    );
  }

  return buildNameValueLabels(
    labelsConfig,
    labelsRect,
    textMeasurer,
    textTruncator,
  );
};

type TextTruncator = (
  text: string,
  fontSize: number,
  availableWidth: number,
  truncationMode: TruncationMode,
  priority: TruncationPriority,
) => string;

/**
 * Creates a `TextTruncator` function bound to the given text measurer.
 * @internal
 * @param measureText - function that returns the rendered pixel width of a string at a given font size.
 * @returns A `TextTruncator` that truncates text to fit within an available width.
 */
const getTextTruncator = (measureText: TextMeasurer): TextTruncator => {
  return (
    text: string,
    fontSize: number,
    availableWidth: number,
    truncationMode: TruncationMode,
    priority: TruncationPriority,
  ) => {
    const measure = (t: string) => measureText(t, fontSize);

    return truncateString(
      text,
      availableWidth,
      {},
      truncationMode,
      priority,
      measure,
    );
  };
};

/**
 * Computes the centered label position within a tree-map node rectangle.
 * @internal
 * @param treemapRect - bounding rectangle of the tree-map node.
 * @returns The `{ x, y }` canvas coordinates for the label origin.
 */
const getLabelPosition = ({ width, height, x, y }: TreeMapRect) => {
  const { centerX, centerY } = getRectCenter(width, height);
  return { x: x + centerX, y: y + centerY };
};

/**
 * Returns the center of the node.
 * @internal
 * @param height - height of the node.
 * @param width - width of the node.
 */
const getRectCenter = (width: number, height: number) => ({
  centerX: width / 2,
  centerY: height / 2,
});

/**
 * Builds a single bold label containing only the value text.
 * Truncates the text when it exceeds the available width.
 * @internal
 * @param labelsConfig - display configuration providing the value, text size, and truncation mode.
 * @param labelRect - bounding rectangle available for the label.
 * @param measureText - function used to measure rendered text width.
 * @param truncateText - function used to truncate text to a given width.
 * @returns A single-element `TreeMapLabel` array.
 */
const buildValueLabel = (
  labelsConfig: LabelsConfig,
  labelRect: TreeMapRect,
  measureText: TextMeasurer,
  truncateText: TextTruncator,
) => {
  const { value, textSize, truncationMode } = labelsConfig;
  const { width, x, y } = labelRect;
  const fontConfig = getFontConfig(value, textSize, width, measureText);
  const valueWidth = measureText(value, fontConfig.fontSize);
  const boldFontConfig = getBoldFontConfig(fontConfig);
  const text =
    valueWidth > width
      ? getTruncatedText(
          width,
          value,
          fontConfig.fontSize,
          truncationMode,
          truncateText,
        )
      : value;

  return [createLabel(text, boldFontConfig, { x, y })];
};

/**
 * Derives a `FontConfig` for the given text and available width.
 * When `textSize` is `'auto'`, the largest fitting preset size is selected.
 * @internal
 * @param text - the string that needs to fit within `availableWidth`.
 * @param textSize - explicit font size in pixels, or `'auto'` for automatic sizing.
 * @param availableWidth - maximum pixel width the text must fit in.
 * @param measureText - function used to measure rendered text width.
 * @returns A `FontConfig` with `fontSize`, `lineHeight`, and default `fontWeight`.
 */
const getFontConfig = (
  text: string,
  textSize: 'auto' | number,
  availableWidth: number,
  measureText: TextMeasurer,
): FontConfig => {
  const fontSize =
    textSize === 'auto'
      ? getAutoFontSize(text, availableWidth, measureText)
      : textSize;
  const lineHeight = fontSize * 1.25;

  return { fontSize, lineHeight, fontWeight: DEFAULT_FONT_WEIGHT };
};

/**
 * Selects the largest font size from `AUTO_TEXT_SIZE_PRESETS` at which `text` fits within `availableWidth`.
 * Falls back to `DEFAULT_LABEL_FONTSIZE` when no preset fits.
 * @internal
 * @param text - the string to measure.
 * @param availableWidth - maximum pixel width the text must fit in.
 * @param measureText - function used to measure rendered text width.
 * @returns The chosen font size in pixels.
 */
const getAutoFontSize = (
  text: string,
  availableWidth: number,
  measureText: TextMeasurer,
): number => {
  return (
    AUTO_TEXT_SIZE_PRESETS.find((fontSize) => {
      const valueWidth = measureText(text, fontSize);
      return valueWidth <= availableWidth;
    }) ?? DEFAULT_LABEL_FONTSIZE
  );
};

/**
 * Returns a copy of `fontConfig` with the font weight increased to `INCREASED_FONT_WEIGHT`.
 * @internal
 * @param fontConfig - base font configuration to derive from.
 * @returns A new `FontConfig` with a heavier font weight.
 */
const getBoldFontConfig = (fontConfig: FontConfig) => {
  return { ...fontConfig, fontWeight: INCREASED_FONT_WEIGHT };
};

/**
 * Truncates `value` to fit within `availableWidth` at the given `fontSize`.
 * Returns an empty string when truncation produces only an ellipsis character.
 * @internal
 * @param availableWidth - maximum pixel width allowed for the text.
 * @param value - the string to truncate.
 * @param fontSize - font size in pixels used when measuring.
 * @param truncationMode - strategy controlling where the ellipsis is inserted.
 * @param truncateText - the bound truncation function.
 * @returns The truncated string, or an empty string if nothing meaningful remains.
 */
const getTruncatedText = (
  availableWidth: number,
  value: string,
  fontSize: number,
  truncationMode: TruncationMode,
  truncateText: TextTruncator,
): string => {
  const truncatedText = truncateText(
    value,
    fontSize,
    availableWidth,
    truncationMode,
    'start',
  );

  return truncatedText === ELLIPSIS_CHARACTER ? '' : truncatedText;
};

/**
 * Builds one or two labels for a node that displays both a name and a value.
 * Chooses a layout (multi-line, inline, name-only, ellipsis, or fallback) based
 * on the available space and measured text widths.
 * @internal
 * @param labelsConfig - display configuration providing name, value, text size, and truncation mode.
 * @param labelsRect - bounding rectangle available for the labels.
 * @param measureText - function used to measure rendered text width.
 * @param truncateText - function used to truncate text to a given width.
 * @returns An array of `TreeMapLabel` objects (0–2 items).
 */
const buildNameValueLabels = (
  labelsConfig: LabelsConfig,
  labelsRect: TreeMapRect,
  measureText: TextMeasurer,
  truncateText: TextTruncator,
) => {
  const { name, value, textSize } = labelsConfig;
  const { width, height } = labelsRect;
  const referenceText = getLongestText(name, value);
  const fontConfig = getFontConfig(referenceText, textSize, width, measureText);

  const nameWidth = measureText(name, fontConfig.fontSize);
  const valueWidth = measureText(value, fontConfig.fontSize);
  const spaceWidth = measureText(' ', fontConfig.fontSize);
  const inlineHeight = getInlineHeight(fontConfig.lineHeight);

  const labelMeasurements = {
    inlineHeight,
    multiLineHeight: inlineHeight * 2,
    nameWidth,
    valueWidth,
    nameValueWidth: nameWidth + spaceWidth + valueWidth,
  };
  const layout = determineLabelLayout(labelMeasurements, width, height);

  return buildLabelsForLayout(
    labelsConfig,
    labelsRect,
    layout,
    fontConfig,
    truncateText,
  );
};

/**
 * Computes the total height of a single-line label by adding the default padding to the line height.
 * @internal
 * @param lineHeight - the line height in pixels derived from the font size.
 * @returns The total label height in pixels.
 */
const getInlineHeight = (lineHeight: number) =>
  lineHeight + DEFAULT_LABEL_PADDING;

/**
 * Returns whichever of the two strings has the greater character count.
 * @internal
 * @param a - first string.
 * @param b - second string.
 * @returns The longer string (`a` when lengths are equal).
 */
const getLongestText = (a: string, b: string): string => {
  return a.length > b.length ? a : b;
};

interface LabelMeasurements {
  inlineHeight: number;
  multiLineHeight: number;
  nameWidth: number;
  valueWidth: number;
  nameValueWidth: number;
}

/**
 * Determines the best `LabelLayout` variant for the available space.
 * Priority order: `Multi` → `Inline` → `Name` → `Ellipsis` → `Fallback`.
 * @internal
 * @param measurements - pre-computed width and height metrics for the label text.
 * @param availableWidth - pixel width available for label rendering.
 * @param containerHeight - pixel height available for label rendering.
 * @returns The most detailed `LabelLayout` that fits within the given dimensions.
 */
const determineLabelLayout = (
  measurements: LabelMeasurements,
  availableWidth: number,
  containerHeight: number,
): LabelLayout => {
  const { multiLineHeight, inlineHeight, nameValueWidth, nameWidth } =
    measurements;

  if (multiLineHeight <= containerHeight) {
    return LabelLayout.Multi;
  }

  if (inlineHeight > containerHeight) {
    return LabelLayout.Fallback;
  }

  if (nameValueWidth <= availableWidth) {
    return LabelLayout.Inline;
  }

  if (nameWidth <= availableWidth) {
    return LabelLayout.Name;
  }

  return LabelLayout.Ellipsis;
};

/**
 * Produces the final `TreeMapLabel` array for a given `LabelLayout`.
 * Each layout variant determines how many labels are created and how they are positioned.
 * @internal
 * @param labelsConfig - display configuration providing name, value, and truncation mode.
 * @param labelsRect - bounding rectangle used to compute label positions.
 * @param layout - the layout variant selected by `determineLabelLayout`.
 * @param fontConfig - base font configuration shared by all labels in this node.
 * @param truncateText - function used to truncate text in the `Ellipsis` layout.
 * @returns An array of `TreeMapLabel` objects (0–2 items depending on the layout).
 */
const buildLabelsForLayout = (
  labelsConfig: LabelsConfig,
  labelsRect: TreeMapRect,
  layout: LabelLayout,
  fontConfig: FontConfig,
  truncateText: TextTruncator,
): TreeMapLabel[] => {
  const { name, value, truncationMode } = labelsConfig;
  const { width, x, y } = labelsRect;
  const labelPosition = { x, y };

  switch (layout) {
    case LabelLayout.Multi: {
      const offsetY = getInlineHeight(fontConfig.lineHeight) / 2;
      const boldFontConfig = getBoldFontConfig(fontConfig);
      const truncatedName = getTruncatedText(
        width,
        name,
        fontConfig.fontSize,
        truncationMode,
        truncateText,
      );
      const truncatedValue = getTruncatedText(
        width,
        value,
        fontConfig.fontSize,
        truncationMode,
        truncateText,
      );

      return [
        createLabel(truncatedName, fontConfig, {
          x: x,
          y: y - offsetY,
        }),
        createLabel(truncatedValue, boldFontConfig, {
          x: x,
          y: y + offsetY,
        }),
      ];
    }

    case LabelLayout.Inline:
      return [createLabel(`${name} ${value}`, fontConfig, labelPosition)];

    case LabelLayout.Name:
      return [createLabel(name, fontConfig, labelPosition)];

    case LabelLayout.Ellipsis: {
      const truncatedText = getTruncatedText(
        width,
        name,
        fontConfig.fontSize,
        truncationMode,
        truncateText,
      );

      return [createLabel(truncatedText, fontConfig, labelPosition)];
    }

    case LabelLayout.Fallback:
    default:
      return [];
  }
};

/**
 * Constructs a `TreeMapLabel` from its constituent parts.
 * @internal
 * @param text - the string to render.
 * @param fontConfig - font size, weight, and line height for this label.
 * @param position - canvas `{ x, y }` coordinates for the label origin.
 * @returns A fully formed `TreeMapLabel` object.
 */
const createLabel = (
  text: string,
  fontConfig: FontConfig,
  position: { x: number; y: number },
): TreeMapLabel => {
  return { text, fontConfig, position };
};
